import React from 'react';
import cn from 'classnames';
import {createUniqueId} from "~/utils";
import UIToast from "~/old-ui/UIToast";
import {createUseStyles} from "react-jss";

const ToastNotificationListContext = React.createContext({
  push: () => null
});

const ToastNotificationListItemsContext = React.createContext({
  items: [],
});

function toastNotificationListReducer(state, action) {
  switch (action.type) {
    case 'PUSH':
      return [
        ...state,

        {
          id: action.itemId,
          item: action.item,
          expireAt: action.expireAt
        }
      ];

    case 'UPDATE':
      const itemIndex = state.findIndex((item) => item.id === action.itemId);

      if (itemIndex === -1) {
        if (action.pushIfNone) {
          return [
            ...state,
            {
              id: action.itemId,
              item: action.item,
              expireAt: action.expireAt
            }
          ]
        } else {
          return state;
        }
      }

      const item = state[itemIndex];

      return [].concat(
        state.slice(0, itemIndex),
        [
          {
            id: action.itemId,
            item: action.extend ? {...item, ...action.item} : action.item,
            expireAt: action.expireAt
          }
        ],
        state.slice(itemIndex + 1)
      );

    case 'DELETE':
      return state.filter(item => (item.id !== action.itemId));

    case 'DELETE_EXPIRED':
      return state.filter(item => !item.expireAt || item.expireAt > Date.now());

    case 'CLEAR':
      return [];

    default:
      throw 'NotImplemented';
  }
}

export function ToastNotificationListProvider({children}) {
  const [toastListItems, dispatchToastListUpdate] = React.useReducer(toastNotificationListReducer, []);

  const pushNotification = React.useCallback(({expire, ...item}) => {
    const itemId = createUniqueId();

    dispatchToastListUpdate({
      type: 'PUSH',
      itemId: itemId,
      item: item,
      expireAt: (expire && expire !== -1) ? Date.now() + expire : null
    });

    const itemWrapper = {
      id: () => itemId,

      update: ({extend = false, force = false, expire = -1, ...newProps}) => {
        dispatchToastListUpdate({
          type: 'UPDATE',
          itemId: itemId,
          item: newProps,
          extend: extend,
          pushIfNone: force,
          expireAt: (expire && expire !== -1) ? Date.now() + expire : null
        });

        return itemWrapper;
      },

      delete: () => {
        dispatchToastListUpdate({
          type: 'DELETE',
          itemId: itemId,
        });

        return itemWrapper;
      }
    };

    return itemWrapper
  }, []);

  const shouldCheckExpired = toastListItems.filter(item => !!item.expireAt).length > 0;
  React.useEffect(() => {
    if (toastListItems.length > 0) {
      const int = setInterval(() => {
        dispatchToastListUpdate({type: 'DELETE_EXPIRED'})
      }, 1000);

      return () => clearInterval(int);
    }
  }, [shouldCheckExpired]);

  const providerValueRef = React.useRef({
    push: pushNotification
  });

  return (
    <ToastNotificationListContext.Provider value={providerValueRef.current}>
      <ToastNotificationListItemsContext.Provider value={{
        items: toastListItems,
        dispatchUpdate: dispatchToastListUpdate,
      }}>
        {children}
      </ToastNotificationListItemsContext.Provider>
    </ToastNotificationListContext.Provider>
  )
}

export function ToastNotificationListArea({className, ...props}) {
  const classes = useStyles();

  const {items, dispatchUpdate} = React.useContext(ToastNotificationListItemsContext);

  const deleteToast = React.useCallback((itemId) => {
    dispatchUpdate({
      type: 'DELETE',
      itemId: itemId,
    })
  }, [dispatchUpdate]);

  return (
    <div className={cn(classes.root, className)} {...props}>
      {items.map(({id, item: props}) => (
        <UIToast
          key={id}
          className={classes.toast}
          closable
          onClose={() => deleteToast(id)}

          {...props}
        />
      ))}
    </div>
  );
}

export function useToastNotifications() {
  return React.useContext(ToastNotificationListContext);
}

const useStyles = createUseStyles({
  root: {},

  toast: {
    width: 320,

    '& + &': {
      marginTop: 12,
    }
  }
});
